import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["role", "aria-label", "title"]
const _hoisted_2 = ["innerHTML"]

import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue'

type FillMap = Record<string, string>


export default /*@__PURE__*/_defineComponent({
  __name: 'DynamicSvg',
  props: {
    src: {},
    svg: {},
    fill: {},
    selector: { default: 'path' },
    replaceExisting: { type: Boolean, default: true },
    size: {},
    width: {},
    height: {},
    preserveAspectRatio: {},
    wrapperClass: {},
    wrapperStyle: {},
    role: { default: 'img' },
    ariaLabel: {},
    title: {}
  },
  emits: ["loaded", "error"],
  setup(__props: any, { emit: __emit }) {

/* eslint-disable no-unused-vars */
const props = __props

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const emit = __emit

const svgContent = ref<string>('')

const computedSize = computed(() => {
  const s = props.size
  if (s == null) return undefined
  return typeof s === 'number' ? s : parseFloat(String(s))
})

const widthPx = computed(() => {
  if (props.width != null)
    return typeof props.width === 'number' ? `${props.width}px` : String(props.width)
  if (computedSize.value != null) return `${computedSize.value}px`
  return undefined
})

const heightPx = computed(() => {
  if (props.height != null)
    return typeof props.height === 'number' ? `${props.height}px` : String(props.height)
  if (computedSize.value != null) return `${computedSize.value}px`
  return undefined
})

function applyFillsToSvg(svgEl: SVGSVGElement) {
  const { fill, selector, replaceExisting } = props
  if (!fill) return

  const setFill = (nodeList: NodeList, color: string) => {
    nodeList.forEach((node) => {
      if (!(node instanceof SVGElement)) return
      const hasExisting = node.getAttribute('fill')
      if (!hasExisting || replaceExisting) {
        node.setAttribute('fill', color)
      }
    })
  }

  if (typeof fill === 'string') {
    const nodes = svgEl.querySelectorAll(selector || 'path')
    setFill(nodes, fill)
  } else {
    for (const [sel, color] of Object.entries(fill)) {
      if (!color) continue
      const nodes = svgEl.querySelectorAll(sel)
      setFill(nodes, color)
    }
  }
}

function applySizing(svgEl: SVGSVGElement) {
  if (widthPx.value) svgEl.setAttribute('width', widthPx.value)
  if (heightPx.value) svgEl.setAttribute('height', heightPx.value)
  if (props.preserveAspectRatio)
    svgEl.setAttribute('preserveAspectRatio', props.preserveAspectRatio)
}

function serialize(doc: Document): string {
  const svg = doc.querySelector('svg') as SVGSVGElement | null
  if (!svg) throw new Error('No <svg> root found')
  applySizing(svg)
  applyFillsToSvg(svg)
  const viewBox = svg.getAttribute('viewBox')
  const w = svg.getAttribute('width')
  const h = svg.getAttribute('height')
  emit('loaded', {
    width: w ? Number(String(w).replace('px', '')) : undefined,
    height: h ? Number(String(h).replace('px', '')) : undefined,
    viewBox,
  })
  return new XMLSerializer().serializeToString(svg)
}

async function loadAndRender() {
  try {
    let raw = props.svg?.trim()
    if (!raw && props.src) {
      if (typeof window === 'undefined') return
      const res = await fetch(props.src)
      raw = await res.text()
    }
    if (!raw) {
      svgContent.value = ''
      return
    }

    const parser = new DOMParser()
    const doc = parser.parseFromString(raw, 'image/svg+xml')

    if (doc.querySelector('parsererror')) {
      throw new Error('Invalid SVG markup provided')
    }

    svgContent.value = serialize(doc)
  } catch (err) {
    emit('error', err)
    svgContent.value = ''
  }
}

onMounted(() => {
  loadAndRender()
})

watch(
    () => [
      props.src,
      props.svg,
      props.fill,
      props.selector,
      props.replaceExisting,
      props.width,
      props.height,
      props.size,
      props.preserveAspectRatio,
    ],
    () => {
      loadAndRender()
    },
    { deep: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", {
    class: _normalizeClass(['inline-block align-middle', __props.wrapperClass]),
    style: _normalizeStyle(__props.wrapperStyle),
    role: __props.role,
    "aria-label": __props.ariaLabel || __props.title || undefined,
    title: __props.title
  }, [
    (svgContent.value)
      ? (_openBlock(), _createElementBlock("span", {
          key: 0,
          innerHTML: svgContent.value
        }, null, 8, _hoisted_2))
      : _renderSlot(_ctx.$slots, "fallback", { key: 1 }, () => [
          _createElementVNode("span", {
            class: "inline-block bg-gray-200",
            style: _normalizeStyle({ width: computedSize.value + 'px', height: computedSize.value + 'px' })
          }, null, 4)
        ])
  ], 14, _hoisted_1))
}
}

})